/**
 * The create form's arithmetic, as pure functions over integer base units.
 *
 * Everything here is `bigint` and strings. Nothing is ever parsed into a JS
 * `number` for arithmetic: the contract carries amounts as base-unit strings
 * beside `{symbol, decimals}` (G8), and the one place a `number` is legitimate
 * is `quantityMilli`, which the contract itself types as an integer count of
 * thousandths and which is range-checked before it is used.
 *
 * The two refusals the *client* raises here are the two the service raises for
 * the same input, and they are raised for the same reason — so the operator
 * sees one answer, not a client answer that the server then contradicts:
 *
 *  - `AMOUNT_PRECISION`: `lineTotal = quantityMilli * unitPrice / 1000` must be
 *    a whole base unit. Rounding it here would put a total on screen that the
 *    ledger does not hold.
 *  - `INVALID_AMOUNT`: an amount that is not a decimal, or a discount that
 *    would drive the total below zero (contract amounts are non-negative).
 *
 * Everything else — the payer, the asset, the network floors, the due date — is
 * validated by the service, and this module only produces the *inputs* to those
 * checks (`checkNetworkFloor`, `isPastDueDate`) so the page can warn without
 * pretending to be the authority.
 */

import {
  MoneyError,
  addBaseUnits,
  compareBaseUnits,
  money,
  parseBaseUnits,
  parseDecimalToBaseUnits,
  subtractBaseUnits,
} from "@/features/invoicing/money";
import type { Asset, LineInput, LineKind, NetworkLimit } from "@/features/invoicing/types";
import { COPY } from "./copy";

/**
 * Quantities are thousandths on the wire (`quantityMilli`). A quantity typed
 * with more than three decimals cannot be represented and is refused rather
 * than truncated.
 */
export const QUANTITY_DECIMALS = 3;
const QUANTITY_SCALE = 1000n;

/** One editable line. Values are the strings the operator typed, not wire shapes. */
export interface DraftLine {
  /** Local row identity. Never sent: the wire has no line id on create. */
  id: string;
  kind: LineKind;
  description: string;
  /** ITEM only — the quantity as typed, e.g. `"2.5"`. */
  quantity: string;
  /** ITEM only — the unit price as typed, e.g. `"1,250.50"`. */
  unitPrice: string;
  /** TAX and DISCOUNT only — the flat amount as typed. */
  amount: string;
}

/** A refusal raised by this module. `code` is one the contract already uses. */
export interface LineError {
  code: "INVALID_AMOUNT" | "AMOUNT_PRECISION";
  message: string;
}

/** One line after pricing: its total, its wire form, and any refusal. */
export interface PricedLine {
  id: string;
  kind: LineKind;
  description: string;
  /** Null when the line could not be priced. */
  lineTotal: ReturnType<typeof money> | null;
  /** Null when the line could not be priced — the wire never sees a partial line. */
  input: LineInput | null;
  error: LineError | null;
}

/** The whole form, priced. */
export interface InvoicePreview {
  lines: PricedLine[];
  subtotal: ReturnType<typeof money>;
  tax: ReturnType<typeof money>;
  discount: ReturnType<typeof money>;
  total: ReturnType<typeof money>;
  /** Every line priced and the total is not below zero. */
  complete: boolean;
  /**
   * The `lines` array of the create/update body. Only ever the priced lines, so
   * a form with one broken line submits nothing rather than a partial invoice.
   */
  lineInputs: LineInput[];
  /** `discount > subtotal + tax`: the service refuses a negative total. */
  totalBelowZero: boolean;
}

function failure(code: LineError["code"], message: string): LineError {
  return { code, message };
}

/**
 * A quantity as typed -> thousandths. Reuses the shared decimal parser at three
 * decimals, so thousands separators behave the same as they do for an amount,
 * and remaps the message: "this asset has 3 decimal places" is true but
 * confusing on a quantity field, which is not denominated in the asset.
 */
export function parseQuantityMilli(value: string): { ok: true; milli: bigint } | { ok: false; error: LineError } {
  try {
    return { ok: true, milli: parseBaseUnits(parseDecimalToBaseUnits(value, QUANTITY_DECIMALS)) };
  } catch (error) {
    if (error instanceof MoneyError) {
      return {
        ok: false,
        error: failure(
          error.code,
          error.code === "AMOUNT_PRECISION"
            ? "Quantity accepts at most three decimal places."
            : "Enter a quantity as a decimal number, e.g. 2.5.",
        ),
      };
    }
    throw error;
  }
}

/** An amount as typed -> base units for the chosen asset. */
function parseAmount(value: string, asset: Asset): { ok: true; base: bigint } | { ok: false; error: LineError } {
  try {
    return { ok: true, base: parseBaseUnits(parseDecimalToBaseUnits(value, asset.decimals)) };
  } catch (error) {
    if (error instanceof MoneyError) {
      return { ok: false, error: failure(error.code, error.message) };
    }
    throw error;
  }
}

/**
 * Price one row against the asset. The row's own description is validated here
 * too: the contract requires one, and an empty one would create a line on the
 * wire that no operator can read back.
 */
export function priceDraftLine(row: DraftLine, asset: Asset): PricedLine {
  const description = row.description.trim();
  const shell = { id: row.id, kind: row.kind, description };
  const failed = (error: LineError): PricedLine => ({
    ...shell,
    lineTotal: null,
    input: null,
    error,
  });

  if (description === "") {
    return failed(failure("INVALID_AMOUNT", COPY.descriptionRequired));
  }

  if (row.kind === "ITEM") {
    const quantity = parseQuantityMilli(row.quantity);
    if (!quantity.ok) return failed(quantity.error);
    if (quantity.milli <= 0n) {
      return failed(failure("INVALID_AMOUNT", COPY.quantityRequired));
    }
    // The contract types `quantityMilli` as a JSON number. A quantity past
    // 2^53 thousandths cannot survive that round trip, so it is refused here
    // rather than silently mis-sent.
    if (quantity.milli > BigInt(Number.MAX_SAFE_INTEGER)) {
      return failed(failure("INVALID_AMOUNT", "That quantity is larger than the contract can carry."));
    }
    const unitPrice = parseAmount(row.unitPrice, asset);
    if (!unitPrice.ok) return failed(unitPrice.error);
    if (unitPrice.base <= 0n) {
      return failed(failure("INVALID_AMOUNT", COPY.unitPriceRequired));
    }

    // `lineTotal = quantityMilli * unitPrice / 1000`, integer or refused.
    const product = quantity.milli * unitPrice.base;
    if (product % QUANTITY_SCALE !== 0n) {
      return failed(failure("AMOUNT_PRECISION", COPY.linePrecision));
    }
    const total = product / QUANTITY_SCALE;
    const totalMoney = money(total, asset.symbol, asset.decimals);
    return {
      ...shell,
      lineTotal: totalMoney,
      input: {
        kind: "ITEM",
        description,
        quantityMilli: Number(quantity.milli),
        unitPrice: money(unitPrice.base, asset.symbol, asset.decimals),
      },
      error: null,
    };
  }

  /*
   * TAX and DISCOUNT are *flat* lines: the design spec (INV-01, G14) records
   * both as their own line carrying an amount, not a rate. The contract's
   * `LineInput` accepts either `unitPrice` or `amount` for a TAX line; `amount`
   * is what a flat tax is, so that is what goes on the wire.
   */
  const amount = parseAmount(row.amount, asset);
  if (!amount.ok) return failed(amount.error);
  if (amount.base <= 0n) {
    return failed(failure("INVALID_AMOUNT", COPY.amountRequired));
  }
  const amountMoney = money(amount.base, asset.symbol, asset.decimals);
  return {
    ...shell,
    lineTotal: amountMoney,
    input: { kind: row.kind, description, amount: amountMoney },
    error: null,
  };
}

/** Price every row and roll the totals up, all in base units. */
export function previewInvoice(rows: DraftLine[], asset: Asset): InvoicePreview {
  const lines = rows.map((row) => priceDraftLine(row, asset));

  let subtotalBase = 0n;
  let taxBase = 0n;
  let discountBase = 0n;
  for (const line of lines) {
    if (line.lineTotal === null) continue;
    const base = parseBaseUnits(line.lineTotal.amount);
    if (line.kind === "ITEM") subtotalBase = addBaseUnits(subtotalBase, base);
    else if (line.kind === "TAX") taxBase = addBaseUnits(taxBase, base);
    else discountBase = addBaseUnits(discountBase, base);
  }

  const grossBase = addBaseUnits(subtotalBase, taxBase);
  const totalBelowZero = compareBaseUnits(discountBase, grossBase) > 0;
  const totalBase = totalBelowZero ? 0n : subtractBaseUnits(grossBase, discountBase);

  const lines_ = lines;
  const complete =
    lines_.length > 0 && !totalBelowZero && lines_.every((line) => line.input !== null && line.error === null);

  return {
    lines: lines_,
    subtotal: money(subtotalBase, asset.symbol, asset.decimals),
    tax: money(taxBase, asset.symbol, asset.decimals),
    discount: money(discountBase, asset.symbol, asset.decimals),
    total: money(totalBase, asset.symbol, asset.decimals),
    complete,
    lineInputs: complete ? lines_.map((line) => line.input as LineInput) : [],
    totalBelowZero,
  };
}

/* ------------------------------------------------------------------ floors */

/**
 * Whether the total clears the network's floors, as a message to show beside
 * the total. The service is still the authority — issuing below
 * `minInvoiceAmount` or inside `dustThreshold` is refused server-side with its
 * own code — so this only ever produces a note, never a blocked submit.
 *
 * `limits` is the settings response's `networkLimits`, matched on the network
 * the operator selected. A network with no entry has no configured floor.
 */
export function checkNetworkFloor(totalBase: string, limits: NetworkLimit[], network: string): string | null {
  const limit = limits.find((entry) => entry.network === network);
  if (!limit) return null;
  const total = parseBaseUnits(totalBase);
  if (compareBaseUnits(total, limit.minInvoiceAmount.amount) < 0) {
    return `Below the minimum invoice amount for this network (${limit.minInvoiceAmount.amount} base units).`;
  }
  if (compareBaseUnits(total, limit.dustThreshold.amount) <= 0) {
    return `At or below this network's dust threshold (${limit.dustThreshold.amount} base units).`;
  }
  return null;
}

/* -------------------------------------------------------------- date guard */

/**
 * `DUE_DATE_PAST`, as the client can see it. Compared on the calendar date
 * only: the service compares instants, so a due date of "today" may still be
 * refused depending on the time — which is exactly why this returns a warning
 * and not a hard block.
 */
export function isPastDueDate(dueDate: string, today: string): boolean {
  if (!dueDate) return false;
  return dueDate < today;
}

/** Today as `YYYY-MM-DD` in the operator's own time zone. */
export function todayIsoDate(now: Date = new Date()): string {
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}
